"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, House as Home, Store, Building2, ArrowRight, MapPin } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
export default function SafetyAccess() {
  const shouldReduceMotion = useReducedMotion();
  const accessEnvironments = [{
    id: "env-1",
    icon: Home,
    title: "Suburban residential streets",
    description: "Careful footing layout on driveways, pedestrian-safe ground clearances, and tidy protections for residential cul-de-sacs and private properties across Camberley."
  }, {
    id: "env-2",
    icon: Store,
    title: "Local commercial parades",
    description: "Pavement gantry structures, crash decks, and debris netting designed to keep shopfronts open and daily foot traffic moving underneath safely."
  }, {
    id: "env-3",
    icon: Building2,
    title: "Nearby business parks",
    description: "Scheduled deliveries, robust perimeter hoarding, and coordinated access paths built to respect active loading bays, car parks, and commercial operations."
  }];
  const safetySteps = [{
    id: "step-1",
    number: "01",
    title: "Site assessment & survey",
    description: "Detailed inspection of ground conditions, overhead hazards, vehicular routes, and building fabric before any tube or fitting arrives."
  }, {
    id: "step-2",
    number: "02",
    title: "Safe access installation",
    description: "Erected to rigorous standards using properly tied tube and fitting systems, ladder access traps, edge protection, and double guardrails."
  }, {
    id: "step-3",
    number: "03",
    title: "Handover inspection & tagging",
    description: "Formal safety sign-off, Scafftag issue, and clear load-rating documentation provided directly to the site manager or property owner."
  }];
  const accreditations = [{
    name: "NASC",
    full: "National Access & Scaffolding Confederation",
    note: "Audited industry standards for training, equipment, and operational practices."
  }, {
    name: "CHAS",
    full: "Contractors Health and Safety Assessment Scheme",
    note: "Accredited compliance in health, safety management, and risk mitigation."
  }];
  const nearbyAreas = [{
    name: "Farnborough",
    to: "/scaffolding-in-farnborough"
  }, {
    name: "Woking",
    to: "/scaffolding-in-woking"
  }, {
    name: "Fleet",
    to: "/scaffolding-in-fleet"
  }];
  return <section id="camberleysafetyaccess" data-nav="light" className="bg-background text-foreground py-20 md:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Top Header Block */}
        <div className="max-w-3xl mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-primary/30 text-primary">
            Camberley (GU15) & Surrey Heath
          </Badge>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-default font-bold tracking-tight text-foreground mb-6">
            Scaffolding built for Camberley streets and sites
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-sans leading-relaxed">
            Delivering safe, certified scaffolding across Camberley&apos;s principal GU15 postal district, with surrounding coverage into neighbouring GU16–GU17 borders for domestic, commercial, and industrial projects.
          </p>
        </div>

        {/* Camberley Access Environments Grid */}
        <div className="mb-20">
          <div className="mb-8">
            <h3 className="text-xl sm:text-2xl font-default font-semibold text-foreground">
              Local site environments
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">
              Practical planning tailored to how Camberley spaces work every day.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {accessEnvironments.map((env, i) => {
            const IconComp = env.icon;
            return <motion.div key={env.id} data-index={i} initial={shouldReduceMotion ? false : {
              opacity: 0,
              y: 16
            }} whileInView={shouldReduceMotion ? undefined : {
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.1
            }} className="h-full">
                  <Card className="h-full border border-border bg-card shadow-sm hover:border-primary/40 transition-[border-color,box-shadow]">
                    <CardHeader>
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-3">
                        <IconComp className="size-6" />
                      </div>
                      <CardTitle className="text-lg font-default font-semibold text-card-foreground">
                        {env.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm font-sans leading-relaxed text-muted-foreground">
                        {env.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Safety Process & Accreditations Two-Column Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Safety Workflow Steps */}
          <div className="lg:col-span-7 space-y-8">
            <div>
              <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground mb-3">
                Structured safety from first survey to final strike
              </h3>
              <p className="text-muted-foreground font-sans leading-relaxed">
                Every structure follows documented procedures to protect trades on the platform, occupants inside the building, and the general public below.
              </p>
            </div>

            <div className="space-y-4">
              {safetySteps.map((step, i) => <div key={step.id} data-index={i} className="flex gap-4 p-5 rounded-xl border border-border bg-card/60 shadow-xs">
                  <div className="shrink-0 flex items-center justify-center size-10 rounded-lg bg-muted text-foreground font-mono font-bold text-sm">
                    {step.number}
                  </div>
                  <div>
                    <h4 className="text-base font-default font-semibold text-foreground mb-1">
                      {step.title}
                    </h4>
                    <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Right Column: Verified Accreditations & Site Compliance */}
          <div className="lg:col-span-5">
            <Card className="border border-border bg-card shadow-md p-2">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2 text-primary mb-2">
                  <ShieldCheck className="size-6" />
                  <span className="text-xs font-semibold tracking-wide uppercase font-mono">
                    Accredited Compliance
                  </span>
                </div>
                <CardTitle className="text-xl font-default font-bold text-card-foreground">
                  NASC & CHAS accredited
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  Our operations and site safety standards are validated through recognized national governing bodies.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4 pt-2">
                {accreditations.map((acc, i) => <div key={acc.name} data-index={i} className="p-4 rounded-lg bg-muted/50 border border-border">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-default font-bold text-base text-foreground">
                        {acc.name}
                      </span>
                      <Badge variant="secondary" className="text-xs font-mono">
                        Confirmed
                      </Badge>
                    </div>
                    <p className="text-xs font-semibold text-foreground/80 mb-1">
                      {acc.full}
                    </p>
                    <p className="text-xs text-muted-foreground font-sans">
                      {acc.note}
                    </p>
                  </div>)}

                <div className="pt-2 border-t border-border flex flex-col sm:flex-row gap-3">
                  <Button asChild className="w-full sm:w-auto">
                    <Link to="/ContactRequestQuotePage">
                      Request a quote
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="w-full sm:w-auto">
                    <Link to="/HealthSafetyAccreditationsPage">
                      View safety standards
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Coverage Districts & Surrounding Areas Block */}
        <div className="mt-16 md:mt-20 pt-10 border-t border-border">
          <div className="rounded-xl border border-border bg-card p-6 md:p-8 shadow-xs">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div className="max-w-2xl">
                <div className="flex items-center gap-2 text-primary font-mono text-xs font-semibold uppercase tracking-wider mb-2">
                  <MapPin className="size-4" />
                  <span>Local Coverage & Postcodes</span>
                </div>
                <h3 className="text-lg md:text-xl font-default font-bold text-foreground mb-2">
                  Principal Camberley coverage in GU15 with surrounding support
                </h3>
                <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                  GU15 is our principal Camberley district, covering the town centre, Heatherside, and Crawley Ridge. Surrounding area coverage extends into adjoining GU16 (Frimley) and GU17 (Blackwater) where project requirements connect across the Surrey Heath border.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
                <div className="flex flex-wrap items-center gap-2">
                  {nearbyAreas.map((area, i) => <Button key={area.name} asChild variant="outline" size="sm" className="h-9" data-index={i}>
                      <Link to={area.to}>
                        {area.name}
                      </Link>
                    </Button>)}
                </div>
                <Button asChild variant="default" size="sm" className="h-9">
                  <Link to="#">
                    View all areas
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}